import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import classnames from 'classnames/bind';
import PropTypes from 'prop-types';
import { PageContainer } from '../../components/PageContainer';
import CustomLink from '../../components/CustomLink';
import { Button } from '../../components/Button';
import { Icon } from '../../components/Icon';
import { InfoModal } from '../../components/InfoModal';
import { SmallNotice } from '../../components/SmallNotice';
import { useUser } from '../../shared/use-user';
import { useAuthentication } from '../../shared/use-authentication';
import { useRouting } from '../../shared/use-routing';
import { pagePathnames } from '../../pages/page-routes';
import { logEvent } from '../../../lib/shared/event-logger';
import { EVENT_TYPES } from '../../../lib/shared/event-types';
import styles from './AccountLayout.css';

const cx = classnames.bind(styles);

const getNavigationItems = () => ([
    {
        id: 1,
        title: 'Subscription',
        iconName: 'premium',
        link: pagePathnames.account,
        eventName: EVENT_TYPES.SUBSCRIPTION_PAGE_VISITED
    },
    {
        id: 2,
        title: 'My purchases',
        iconName: 'shoppingOrder',
        link: pagePathnames.purchases,
        eventName: EVENT_TYPES.PURCHASES_PAGE_VISITED
    },
    {
        id: 4,
        title: 'Payment history',
        iconName: 'creditCard',
        link: pagePathnames.paymentHistory,
        eventName: EVENT_TYPES.PAYMENT_HISTORY_PAGE_VISITED
    },
    {
        id: 5,
        title: 'Delete account',
        iconName: 'trash',
        link: pagePathnames.delete,
        eventName: EVENT_TYPES.DELETE_ACCOUNT_PAGE_VISITED,
        isDesktopOnly: true
    }
]);

function AccountLayout({ children, isPremiumFeature }) {
    const { t } = useTranslation();
    const { userData } = useUser();
    const { signOut } = useAuthentication();
    const { location, buildPath, stripLangPrefix } = useRouting();
    const navigationItems = getNavigationItems();
    const currentPathname = stripLangPrefix(location.pathname);
    const activeNavigationItem = navigationItems.find(navigationItem => navigationItem.link === currentPathname) || {};
    const isDeleteAccountPage = currentPathname === pagePathnames.delete;
    const [isSignOutModalOpen, setIsSignOutModalOpen] = useState(false);

    useEffect(() => {
        if (activeNavigationItem.eventName) {
            logEvent(activeNavigationItem.eventName);
        }
    }, [activeNavigationItem.eventName]);

    const handleSignOut = e => {
        e.preventDefault();
        setIsSignOutModalOpen(true);
    };

    const confirmSignOut = () => {
        logEvent(EVENT_TYPES.SIGN_OUT_CLICKED);
        signOut();
    };

    const renderPremium = className => {
        if (userData.isActiveSubscription || !isPremiumFeature) {
            return null;
        }

        return (
            <div className={cx('premium', className)}>
                <Button
                    label='Upgrade to premium'
                    type='special-1'
                    onClick={() => logEvent(EVENT_TYPES.PREMIUM_BUTTON_CLICKED)}
                    link={{
                        to: buildPath(pagePathnames.premium)
                    }}
                />
                <p className={styles.premiumDescription}>A premium subscription is required to use all features.</p>
            </div>
        );
    };

    return (
        <>
            <InfoModal
                isOpen={isSignOutModalOpen}
                onCloseModal={() => setIsSignOutModalOpen(false)}
                className={cx('modal', 'signOutModal')}
                title='Sign Out?'
                renderButtons={() => (
                    <>
                        <Button
                            onClick={confirmSignOut}
                            label={t('common.signOut')}
                            size='medium'
                        />
                        <Button
                            onClick={() => setIsSignOutModalOpen(false)}
                            label='Cancel'
                            type='secondary'
                            size='medium'
                        />
                    </>
                )}
            >
                <SmallNotice
                    iconName='info'
                    description='Are you sure you want to sign out?'
                    withBackground
                />
            </InfoModal>
            <PageContainer className={styles.root}>
                <div className={styles.mainInformation}>
                    <div className={styles.mainInformationItem}>
                        <p className={styles.informationItemName}>
                            Email
                        </p>
                        <p className={styles.informationItemValue}>
                            {userData.email || '-'}
                        </p>
                    </div>
                    {Boolean(userData.firstName) && (
                        <div className={styles.mainInformationItem}>
                            <p className={styles.informationItemName}>
                                Username
                            </p>
                            <p className={styles.informationItemValue}>
                                {userData.firstName || '-'}
                            </p>
                        </div>
                    )}
                    <Button
                        onClick={handleSignOut}
                        className={styles.buttonSignOut}
                        type='secondary'
                        iconRight={{
                            name: 'exit'
                        }}
                        label={t('common.signOut')}
                    />
                </div>
                <h1 className={styles.title}>My account</h1>
                {renderPremium('mobile')}
                <div className={styles.content}>
                    <nav aria-label='Account sections'>
                        <ul className={styles.navigation}>
                            {navigationItems.map(item => {
                                const isActive = item.link === activeNavigationItem.link;

                                return (
                                    <li key={item.id} className={cx('navigationItem', { navigationItemDesktopOnly: item.isDesktopOnly })}>
                                        <CustomLink
                                            to={buildPath(item.link)}
                                            className={cx('navigationLink', { active: isActive })}
                                        >
                                            <Icon name={item.iconName} className={styles.navigationLinkIcon} aria-hidden='true' />
                                            <span className={styles.navigationLinkLabel}>{item.title}</span>
                                        </CustomLink>
                                    </li>
                                );
                            })}
                        </ul>
                    </nav>
                    <div>
                        {renderPremium('desktop')}
                        {children}
                    </div>
                </div>
                {!isDeleteAccountPage && (
                    <Button
                        label='Delete account'
                        type='secondary'
                        className={styles.deleteAccountButton}
                        iconLeft={{
                            name: 'trash'
                        }}
                        link={{
                            to: buildPath(pagePathnames.delete)
                        }}
                    />
                )}
            </PageContainer>
        </>
    );
}

AccountLayout.propTypes = {
    children: PropTypes.any.isRequired,
    isPremiumFeature: PropTypes.bool
};

AccountLayout.defaultProps = {
    isPremiumFeature: false
};

export {
    AccountLayout
};
